// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Eye, Car, Activity, 
  AlertCircle, CheckCircle, XCircle
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Summary Card Component
function SummaryCard({ icon: Icon, label, value, loading }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-center gap-4">
        <div 
          className="w-12 h-12 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${VCIN_COLORS.primary}10` }}
        >
          <Icon className="w-6 h-6" style={{ color: VCIN_COLORS.primary }} />
        </div>
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          {loading ? (
            <div className="h-8 w-16 bg-gray-200 rounded animate-pulse" />
          ) : (
            <p className="text-2xl font-bold text-gray-900">{value?.toLocaleString()}</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Platform Health Card
function HealthCard({ activeCompanies, suspendedCompanies, loading }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
        Platform Health
      </h3>
      {loading ? (
        <div className="space-y-3">
          <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
          <div className="h-6 w-full bg-gray-200 rounded animate-pulse" />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm text-gray-700">Active Companies</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">{activeCompanies}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-600" />
              <span className="text-sm text-gray-700">Suspended Companies</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">{suspendedCompanies}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Activity Feed Item - uses VCIN brand blue for login/auth actions
function ActivityItem({ log }) {
  const getActionStyle = (action) => {
    if (action.includes('LOGIN') || action.includes('VERIFIED')) {
      return { bg: `${VCIN_COLORS.primary}15`, color: VCIN_COLORS.primary };
    }
    if (action.includes('CREATED') || action.includes('APPROVED')) {
      return { bg: '#E8F5E9', color: '#2E7D32' };
    }
    if (action.includes('SUSPENDED') || action.includes('REDACTED') || action.includes('SUPPRESSED')) {
      return { bg: '#FFEBEE', color: '#C62828' };
    }
    if (action.includes('UPDATED') || action.includes('REACTIVATED')) {
      return { bg: '#FFF3E0', color: '#E65100' };
    }
    return { bg: '#F5F5F5', color: '#616161' };
  };

  const style = getActionStyle(log.action);
  const timeAgo = getTimeAgo(new Date(log.timestamp));

  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
      <div 
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ backgroundColor: style.bg }}
      >
        <Activity className="w-4 h-4" style={{ color: style.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900">
          <span 
            className="font-medium px-1.5 py-0.5 rounded text-xs"
            style={{ backgroundColor: style.bg, color: style.color }}
          >
            {log.action.replace(/_/g, ' ')}
          </span>
        </p>
        <p className="text-xs text-gray-500 mt-1 truncate">
          {log.user_email} • {log.collection}/{log.record_id?.toString().slice(-6)}
        </p>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo}</span>
    </div>
  );
}

function getTimeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Loading Skeleton for Activity Feed
function ActivitySkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="flex items-start gap-3 py-3 animate-pulse">
          <div className="w-8 h-8 rounded-full bg-gray-200" />
          <div className="flex-1">
            <div className="h-4 w-32 bg-gray-200 rounded mb-2" />
            <div className="h-3 w-48 bg-gray-200 rounded" />
          </div>
          <div className="h-3 w-12 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
}

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const response = await api.get('/admin/dashboard');
        setData(response.data);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="super-admin-dashboard">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Platform Dashboard</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SummaryCard 
          icon={Building2} 
          label="Total Companies" 
          value={data?.summary?.total_companies} 
          loading={loading}
        />
        <SummaryCard 
          icon={Users} 
          label="Total Officers" 
          value={data?.summary?.total_officers} 
          loading={loading}
        />
        <SummaryCard 
          icon={Eye} 
          label="Total Sightings" 
          value={data?.summary?.total_sightings} 
          loading={loading}
        />
        <SummaryCard 
          icon={Car} 
          label="Vehicles in Database" 
          value={data?.summary?.total_vehicles} 
          loading={loading}
        />
      </div>

      {/* Platform Health + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platform Health */}
        <div className="lg:col-span-1">
          <HealthCard 
            activeCompanies={data?.platform_health?.active_companies}
            suspendedCompanies={data?.platform_health?.suspended_companies}
            loading={loading}
          />
        </div>

        {/* Recent Activity Feed */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Recent Activity
          </h3>
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <ActivitySkeleton />
            ) : data?.recent_activity?.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No recent activity</p>
            ) : (
              data?.recent_activity?.map((log) => (
                <ActivityItem key={log.id} log={log} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
